import {computed, Injector, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {L10nTranslationService} from 'angular-l10n';
import {BehaviorSubject, combineLatestWith, defer, merge, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {filter, map, share, startWith, takeUntil, tap} from 'rxjs/operators';
import {bindWith} from '../../common/rxjs/bind-with';
import {DexStreamingInitResolverService} from '../../components/dex/dex-streaming-init-resolver.service';
import {DexComputedTrancheService} from '../../components/dex/dex-streaming/computed/dex-computed-tranche.service';
import {ComputedTranche} from '../../components/dex/dex-streaming/types/computed/computed-tranche';
import {Deliverable} from '../../components/dex/dex-streaming/types/deliverable';
import {numToV, ValueWithDel, valueWithDelToDec, vToDec} from '../../components/dex/dex-streaming/utils';
import {DexTrancheUtilityService} from '../../components/dex/dex-tranche-utility/dex-tranche-utility.service';
import {WIDGET_DESCRIPTION} from '../../components/layout/widget/widget-description';
import {
    WidgetImplementationEvent,
    WidgetImplementationEventFlags
} from '../../components/layout/widget/widget-implementation';
import {Side} from '../../enums/side';
import {
    CommonWidgetImpl,
    getSettingsWithUpdateField,
    getSettingsWithUpdateFields,
    getWidgetSettingsWithUpdatesStream,
    hasSettingUpdateField,
    isSettingsUpdateEmptyOrSame,
    pipeSettingsCorrections,
    setWidgetSettings,
    updateWidgetSettings,
    widgetImplDestroy$
} from '../components/common-widget.implementation';
import {DexLastTradeParamsService} from './dex-last-trade-params.service';
import {DexLpTradeWidgetSettings, DexLpTradeWidgetSettingsService} from './settings';

export class DexLpTradeWidgetImplementation implements CommonWidgetImpl<DexLpTradeWidgetSettings> {
    public readonly widgetDescription = this.injector.get(WIDGET_DESCRIPTION);

    public settings: DexLpTradeWidgetSettings;

    public settingsService: DexLpTradeWidgetSettingsService = this.injector.get(DexLpTradeWidgetSettingsService);

    public readonly ready$ = new BehaviorSubject<boolean>(false);
    public readonly event$ = new Subject<WidgetImplementationEvent>();

    public readonly settingsWithUpdates$: Observable<Partial<DexLpTradeWidgetSettings>> =
                        getWidgetSettingsWithUpdatesStream(this);

    public readonly initialDependency$ = merge(
        this.injector.get(DexStreamingInitResolverService).resolve(),
        this.injector.get(DexLastTradeParamsService).resolve()
    );

    public readonly title$: Observable<string>;

    public readonly $tranche: Signal<ComputedTranche>;

    public readonly dexTrancheUtilityService = this.injector.get(DexTrancheUtilityService);

    private readonly destroy$ = widgetImplDestroy$(this);
    private readonly translationService = this.injector.get(L10nTranslationService);
    private readonly titleSubject$ = new ReplaySubject<string>(1);

    private readonly dexComputedTrancheService = this.injector.get(DexComputedTrancheService);
    private readonly dexLastTradeParamsService = this.injector.get(DexLastTradeParamsService);

    constructor(
        private injector: Injector
    ) {
        this.$tranche = this.createTrancheSignal();

        this.title$ = this.titleSubject$.pipe(
            bindWith(this.listenToLiveTitle()),
            takeUntil(this.destroy$),
            share()
        );
    }

    public setSettings(
        settings?: DexLpTradeWidgetSettings,
        flags?: WidgetImplementationEventFlags
    ): Observable<DexLpTradeWidgetSettings> {
        return setWidgetSettings(this, settings, flags);
    }

    public updateSettings(
        updates?: Partial<DexLpTradeWidgetSettings>,
        flags?: WidgetImplementationEventFlags
    ): Observable<Partial<DexLpTradeWidgetSettings>> {
        return updateWidgetSettings(this, updates, flags);
    }

    public resolveSettings(
        settings: Partial<DexLpTradeWidgetSettings>
    ): Observable<Partial<DexLpTradeWidgetSettings>> {
        return pipeSettingsCorrections<DexLpTradeWidgetSettings>([
            s => this.dexTrancheUtilityService.getTrancheSymbolFix(s, this.settings),
            s => this.getAssetSymbolFix(s, this.settings),
            s => this.getQtyFix(s, this.settings),
            s => this.storeLastParams(s, this.settings)
        ])(settings, this);
    }

    private listenToLiveTitle(): Observable<any> {
        const side$ = this.settingsWithUpdates$.pipe(
            filter(settings => !!settings.side)
        );

        return side$.pipe(
            combineLatestWith(
                this.translationService.onChange().pipe(startWith())
            ),
            tap(() => this.updateTitle()),
            takeUntil(this.destroy$)
        );
    }

    private updateTitle(): void {
        const side = this.settings.side;
        const titleToTranslate: string = (side === Side.BUY)
                ? 'Purchase Tranche Units'
                : 'Redeem Tranche Units';
        const title = this.translationService.translate(titleToTranslate);

        this.titleSubject$.next(title);
    }

    private createTrancheSignal(): Signal<ComputedTranche> {
        const symbol$ = this.settingsWithUpdates$.pipe(
            filter(update => hasSettingUpdateField(update, 'symbol')),
            map(update => getSettingsWithUpdateField(this.settings, update, 'symbol'))
        );

        const $symbol = toSignal(symbol$);

        return computed(() => {
            const symbol = $symbol();

            return this.dexComputedTrancheService.$list()
                .find(tranche => tranche.unitDeliverable.symbol === symbol);
        });
    }

    private isSideUpdated<S extends {side?: Side}>(
        modelUpdate: Partial<S>,
        prevModel: Partial<S>
    ): boolean {
        return !!modelUpdate?.side
            && modelUpdate.side !== prevModel?.side;
    }

    private getAssetSymbolFix<S extends DexLpTradeWidgetSettings>(
        u: Partial<S>,
        prevS: Partial<S>
    ): Observable<{assetSymbol: string} | undefined> {
        return defer(() => {
            const isTrUpdated = this.dexTrancheUtilityService.isSymbolUpdated(u, prevS);
            const isAssetUpdated = this.dexTrancheUtilityService.isAssetSymbolUpdated(u, prevS);
            const isSideUpdated = this.isSideUpdated(u, prevS);

            if (!isTrUpdated && !isAssetUpdated && !isSideUpdated) {
                return of(undefined);
            }

            const side = getSettingsWithUpdateField(this.settings, u, 'side');

            if (side === Side.SELL) {
                return of(undefined);
            }

            const trSymbol = getSettingsWithUpdateField(this.settings, u, 'symbol');
            const tr = this.dexTrancheUtilityService.getTrancheBySymbol(trSymbol);

            if (!tr) {
                return of(undefined);
            }

            const trId = tr.poolTrancheId;
            let assetSymbol = getSettingsWithUpdateField(this.settings, u, 'assetSymbol');

            if (!assetSymbol) {
                const lastParams = this.dexLastTradeParamsService.getForKey(trSymbol, side);
                assetSymbol = lastParams?.assetSymbol;
            }

            let d = assetSymbol
                && this.dexTrancheUtilityService.dexSecuritiesSubjectService.getDeliverableBySymbol(assetSymbol);
            const isAllowed = d ? this.dexTrancheUtilityService.isDelAllowedForBuy(trId, d?.deliverableId) : false;

            if (!isAllowed) {
                d = this.dexTrancheUtilityService.getDefaultDelAllowedForBuy(trId);
            }

            if (d) {
                return of({assetSymbol: d.symbol});
            }

            return of(undefined);
        });
    }

    private getQtyFix<S extends DexLpTradeWidgetSettings>(
        u: Partial<S>,
        prevS: Partial<S>
    ): Observable<{qty: string} | undefined> {
        const wSTrService = this.dexTrancheUtilityService;

        const toResult = (qty: ValueWithDel): Observable<{ qty: string }> =>
            of({qty: valueWithDelToDec(qty).toString()});

        return defer(() => {
            const isTrUpdated = wSTrService.isSymbolUpdated(u, prevS);
            const isAssetUpdated = wSTrService.isAssetSymbolUpdated(u, prevS);
            const isSideUpdated = this.isSideUpdated(u, prevS);
            const isQtyUpdated = !isSettingsUpdateEmptyOrSame(prevS as DexLpTradeWidgetSettings, u, 'qty');

            if (!isTrUpdated && !isAssetUpdated && !isSideUpdated && !isQtyUpdated) {
                return of(undefined);
            }

            const trSymbol = getSettingsWithUpdateField(this.settings, u, 'symbol');
            const tr = wSTrService.getTrancheBySymbol(trSymbol);
            const side = getSettingsWithUpdateField(this.settings, u, 'side');
            let qtyStr = getSettingsWithUpdateField(this.settings, u, 'qty');
            const qtyWasEmpty= !qtyStr;

            if (!qtyStr) {
                const lastParams = this.dexLastTradeParamsService.getForKey(trSymbol, side);
                qtyStr = lastParams?.qty;
            }

            const d: Deliverable = side === Side.BUY
                ? wSTrService.dexSecuritiesSubjectService.getDeliverableBySymbol(
                    getSettingsWithUpdateField(this.settings, u, 'assetSymbol')
                )
                : wSTrService.dexSecuritiesSubjectService.getDeliverableById(tr.unitDeliverableId);

            const qty = qtyStr ? numToV(qtyStr, d.decimalPlaces) : 0n;

            if (side === Side.BUY) {
                const minQty = wSTrService.getMinBuyQty(tr.poolTrancheId, d);

                if (!qty || qty < minQty?.v) {
                    return toResult(minQty);
                } else {
                    const maxQty = wSTrService.getMaxBuyQty(tr.poolTrancheId, d);

                    if (qty > maxQty?.v) {
                        return toResult(maxQty);
                    }
                }
            } else {
                const minQty = 1n;

                if (!qty) {
                    const defaultQty = this.dexTrancheUtilityService.getMinBuyQty(tr.poolTrancheId, d);

                    return toResult(defaultQty);
                } else if (qty < minQty) {
                    const minQtyStr = vToDec(minQty, d.decimalPlaces).toString();

                    return of({qty: minQtyStr});
                }
            }

            if (qtyWasEmpty) {
                return of({qty: valueWithDelToDec({v: qty, d}).toString()});
            }

            return of(undefined);
        });
    }

    private storeLastParams<S extends DexLpTradeWidgetSettings>(
        u: Partial<S>,
        prevS: Partial<S>
    ): Observable<undefined> {
        const isTrUpdated = this.dexTrancheUtilityService.isSymbolUpdated(u, prevS);
        const isAssetUpdated = this.dexTrancheUtilityService.isAssetSymbolUpdated(u, prevS);
        const isSideUpdated = this.isSideUpdated(u, prevS);
        const isQtyUpdated = !isSettingsUpdateEmptyOrSame(prevS as DexLpTradeWidgetSettings, u, 'qty');

        if (isTrUpdated || isAssetUpdated || isSideUpdated || isQtyUpdated) {
            const params = getSettingsWithUpdateFields(prevS as DexLpTradeWidgetSettings, u, [
                'symbol',
                'assetSymbol',
                'side',
                'qty'
            ]);

            this.dexLastTradeParamsService.storeForKey({
                trancheSymbol: params.symbol,
                side:          params.side,
                qty:           params.qty,
                assetSymbol:   params.side === Side.BUY ? params.assetSymbol : undefined
            });
        }

        return of(undefined);
    }
}
